import React, { type ComponentType, ReactElement, useMemo, useCallback } from 'react';
import { VStack, Text } from '@chakra-ui/react';
import type { NovaIconGlyphProps } from 'components/ui/novaIcon';
import { FileIcon, ImageIcon, ScienceIcon } from 'assets/icons/v2';
import { useInfiniteAlgoliaContent } from 'hooks/api/backend/search/useInfiniteAlgoliaContent';
import {
  MedicationSearchResult,
  ParsedArticleSearchResult,
  SearchContentType,
  SearchResultData,
} from '@novaheal/types';
import { VideoSearchResult } from '@novaheal/types/lib/search/videoSearch';
import { ImageSearchResult } from '@novaheal/types/lib/search/imageSearch';
import {
  isArticleSearchResult,
  isImageSearchResult,
  isMedicationSearchResult,
  isVideoSearchResult,
} from 'types/SearchResultWithParsedArticle';
import { parseSearchArticles } from '@novaheal/parsers';
import { SearchResultListAllFragment } from './SearchResultListAllFragment';

type SearchResultWithParsedArticle =
  | MedicationSearchResult
  | ParsedArticleSearchResult
  | VideoSearchResult
  | ImageSearchResult;

type Props = {
  query?: string;
  numOfColumns?: number;
  handleTabChange?: (tabIdx: number) => void;
};

type ResultCountKey = 'lernkarte' | 'medikamente' | 'medien';

type SectionConfig = {
  key: string;
  title: string;
  results: SearchResultWithParsedArticle[];
  Icon: ComponentType<NovaIconGlyphProps>;
  tabIdx: number;
  contentType: ResultCountKey;
};

export const SearchResultListAll = ({
  query,
  numOfColumns = 1,
  handleTabChange,
}: Props): ReactElement => {
  // Use the enhanced hook that supports multi-query for ALL content type
  const { data, isLoading, typeCounts } = useInfiniteAlgoliaContent({
    contentType: [],
    query: query || '',
  });

  // Parse and categorize results
  const categorizedResults = useMemo(() => {
    const allResults: SearchResultData[] = data?.pages.flatMap((page) => page.hits) || [];

    const parsedResults: SearchResultWithParsedArticle[] = [];

    allResults.forEach((item: SearchResultData) => {
      if (item.contentType === SearchContentType.ARTICLE) {
        const parsed = parseSearchArticles([item]);
        if (parsed && parsed.length > 0) {
          parsedResults.push(parsed[0]);
        }
      } else {
        parsedResults.push(item as SearchResultWithParsedArticle);
      }
    });

    return {
      articles: parsedResults.filter(isArticleSearchResult),
      medications: parsedResults.filter(isMedicationSearchResult),
      medien: parsedResults.filter(
        (result) => isImageSearchResult(result) || isVideoSearchResult(result)
      ),
    };
  }, [data]);

  // Use typeCounts from the enhanced hook
  const resultCounts: Record<ResultCountKey, number> = useMemo(
    () => ({
      lernkarte: typeCounts.articles || 0,
      medikamente: typeCounts.medications || 0,
      medien: typeCounts.media || 0,
    }),
    [typeCounts]
  );

  const sectionConfigs: SectionConfig[] = useMemo(
    () => [
      {
        key: 'articles',
        title: 'Lernkarten',
        results: categorizedResults.articles,
        Icon: FileIcon,
        tabIdx: 1,
        contentType: 'lernkarte',
      },
      {
        key: 'medications',
        title: 'Medikamente',
        results: categorizedResults.medications,
        Icon: ScienceIcon,
        tabIdx: 3,
        contentType: 'medikamente',
      },
      {
        key: 'medien',
        title: 'Medien',
        results: categorizedResults.medien,
        Icon: ImageIcon,
        tabIdx: 2,
        contentType: 'medien',
      },
    ],
    [categorizedResults]
  );

  const handleSeeMoreClick = useCallback(
    (tabIdx: number) => {
      if (handleTabChange) {
        handleTabChange(tabIdx);
      }
    },
    [handleTabChange]
  );

  if (isLoading) {
    return (
      <VStack bg="background.canvas" minH="90%" maxH="90%" pt="l" align="center" justify="center">
        <Text>Loading...</Text>
      </VStack>
    );
  }

  const renderedSections = sectionConfigs.map(
    ({ key, results, Icon, tabIdx, title, contentType }) =>
      results.length > 0 ? (
        <React.Fragment key={key}>
          <SearchResultListAllFragment
            searchResults={results}
            count={resultCounts[contentType]}
            query={query}
            numOfColumns={numOfColumns}
            Icon={Icon}
            title={title}
            onSeeMoreClick={() => handleSeeMoreClick(tabIdx)}
          />
        </React.Fragment>
      ) : null
  );

  return (
    <VStack bg="background.canvas" minH="90%" maxH="90%" pt="l" gap={0}>
      <VStack gap="4xl" width="100%">
        {renderedSections}
      </VStack>
    </VStack>
  );
};
